import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../../services/auth';
import {
  SupportTicket,
  SupportTicketCategory,
  SupportTicketImpact,
  SupportTicketRequest,
} from '../../../../shared/interfaces';
import { SUPPORT_CATEGORIES, SUPPORT_IMPACTS } from '../support-labels';

export type SupportTicketFormValue = SupportTicketRequest & { supportTicketId?: string };

@Component({
  selector: 'app-support-ticket-form',
  imports: [ReactiveFormsModule],
  templateUrl: './support-ticket-form.html',
  styleUrl: './support-ticket-form.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportTicketForm implements OnInit {
  /** Ticket being edited; null when creating */
  @Input() ticket: SupportTicket | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);

  readonly categories = SUPPORT_CATEGORIES;
  readonly impacts = SUPPORT_IMPACTS;
  readonly subjectMax = 150;
  readonly descriptionMax = 4000;

  readonly replyEmail = this.authService.getUserInfo()?.email ?? '';

  readonly form = this.fb.nonNullable.group({
    category: this.fb.control<SupportTicketCategory | null>(null, Validators.required),
    subject: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(this.subjectMax)]],
    description: ['', [Validators.required, Validators.minLength(20), Validators.maxLength(this.descriptionMax)]],
    impact: this.fb.nonNullable.control<SupportTicketImpact>('low'),
    contactPhone: ['', [Validators.maxLength(50)]],
  });

  ngOnInit(): void {
    if (this.ticket) {
      this.form.setValue({
        category: this.ticket.category,
        subject: this.ticket.subject,
        description: this.ticket.description,
        impact: this.ticket.impact,
        contactPhone: this.ticket.contactPhone ?? '',
      });
    } else {
      this.form.controls.contactPhone.setValue(this.authService.getUserInfo()?.phone ?? '');
    }
  }

  showError(control: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[control];
    return c.invalid && c.touched;
  }

  length(control: 'subject' | 'description'): number {
    return this.form.controls[control].value.length;
  }

  // Contract used by the shared modal
  isValid(): boolean {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }
    return true;
  }

  getValue(): SupportTicketFormValue | null {
    if (!this.isValid()) return null;
    const value = this.form.getRawValue();
    return {
      supportTicketId: this.ticket?.supportTicketId,
      category: value.category!,
      subject: value.subject.trim(),
      description: value.description.trim(),
      impact: value.impact,
      contactPhone: value.contactPhone.trim() || null,
    };
  }
}
